import { useState } from "react";
import { toast } from "sonner";
import { api, cardImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

const FIELDS = [["photos", "Foto"], ["price", "Precio"], ["name", "Nombre"], ["set_number", "Set / número"], ["condition", "Condición"]];
const COND = { NM: "Near Mint", LP: "Lightly Played", MP: "Moderately Played", HP: "Heavily Played", DMG: "Dañada", SEALED: "Sellado de fábrica / Nuevo" };

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-white/5 last:border-0">
    <span className="text-neutral-500">{k}</span><span className="text-white text-right" data-testid={testid}>{v}</span>
  </div>
);

// Borrador completo de la publicación que se creará sola al aprobar.
export const DraftPanel = ({ c }) => {
  const d = c.pub_draft;
  if (!d) return null;
  return (
    <div className="mt-3 p-3 bg-[#0A0A0A] border border-amber-500/30 rounded text-xs" data-testid={`custom-draft-${c.id}`}>
      <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">Se publica sola al aprobar · revisa estos datos</div>
      <Row k="Precio" v={`$${Number(d.price).toFixed(2)}`} testid={`custom-draft-price-${c.id}`} />
      <Row k="Cantidad" v={d.quantity} />
      <Row k="Condición" v={COND[d.condition] || d.condition} />
      <Row k="Idioma" v={(d.language || "").toUpperCase()} />
      {d.graded && <Row k="Gradeada" v={`${d.graded_house} · ${d.graded_grade}`} testid={`custom-draft-graded-${c.id}`} />}
      {d.factory_seal_ok && <Row k="Precinto" v="Intacto (ver foto)" />}
      <Row k="Envío base" v={`$${Number(d.ship_cost || 0).toFixed(2)}`} />
    </div>
  );
};

const Side = ({ title, img, name, meta, testid }) => (
  <div className="flex-1 min-w-0 p-2 rounded border border-[#262626] bg-black/40" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{title}</div>
    <div className="aspect-[3/4] w-full max-w-[160px] mx-auto rounded overflow-hidden bg-black grid place-items-center">
      {img ? <img src={img} alt={name} className="w-full h-full object-contain" /> : <span className="text-[10px] text-neutral-600">Sin imagen</span>}
    </div>
    <div className="mt-1 text-sm text-white truncate">{name}</div>
    <div className="text-[11px] text-neutral-400 truncate">{meta}</div>
  </div>
);

// El vendedor descartó la coincidencia del catálogo: comparación lado a lado.
export const PossibleDuplicate = ({ c }) => {
  if (!c.possible_duplicate) return null;
  const m = c.dismissed_match || {};
  const own = c.identification_photos?.[0] ? `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${c.identification_photos[0]}` : null;
  return (
    <div className="mt-3 p-3 rounded border border-[#FF4438]/60 bg-[#FF4438]/5" data-testid={`custom-possible-dup-${c.id}`}>
      <span className="inline-block text-[10px] font-bold tracking-wider px-2 py-0.5 rounded bg-[#FF4438] text-white">POSIBLE DUPLICADO</span>
      <div className="mt-2 text-xs text-neutral-300">Motivo del vendedor: <b className="text-white" data-testid={`custom-dup-reason-${c.id}`}>{c.duplicate_reason}</b></div>
      <div className="mt-2 flex gap-2">
        <Side title="Solicitud" img={own} name={c.name_es} meta={[c.set_name, c.number && `#${c.number}`].filter(Boolean).join(" · ")} testid={`custom-dup-own-${c.id}`} />
        <Side title={`Descartada (${m.source || "catálogo"})`} img={m.image_url ? cardImageUrl(m.image_url, 200) : null} name={m.name_es}
          meta={[m.set_name, m.number && `#${m.number}`, m.year].filter(Boolean).join(" · ")} testid={`custom-dup-match-${c.id}`} />
      </div>
    </div>
  );
};

export const NeedsInfoNote = ({ c }) => c.needs_info ? (
  <div className="mt-2 text-xs text-[#FF6B5E]" data-testid={`custom-needs-info-${c.id}`}>
    Falta dato ({FIELDS.find(([k]) => k === c.needs_info.field)?.[1]}): {c.needs_info.reason}
  </div>
) : null;

export const RequestInfoButton = ({ c, onDone }) => {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState("price");
  const [reason, setReason] = useState("");
  const send = async () => {
    try {
      await api.post(`/admin/custom-cards/${c.id}/request-info`, { field, reason });
      toast.success("Corrección pedida"); setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo pedir la corrección"); }
  };
  if (!open) {
    return <Button size="sm" variant="outline" className="border-amber-500/60 text-amber-300" onClick={() => setOpen(true)} data-testid={`custom-request-info-${c.id}`}>Pedir corrección</Button>;
  }
  return (
    <div className="w-full flex flex-wrap items-center gap-2 p-2 bg-[#0A0A0A] border border-amber-500/30 rounded" data-testid={`custom-request-info-form-${c.id}`}>
      <select value={field} onChange={(e) => setField(e.target.value)} className="h-9 bg-[#171717] border border-[#262626] rounded px-2 text-sm" data-testid={`custom-request-info-field-${c.id}`}>
        {FIELDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo para el vendedor" className="flex-1 min-w-[180px] h-9 bg-[#171717] border border-[#262626] rounded px-2 text-sm" data-testid={`custom-request-info-reason-${c.id}`} />
      <Button size="sm" onClick={send} disabled={!reason.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid={`custom-request-info-send-${c.id}`}>Enviar</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
    </div>
  );
};

// Candado B: posibles duplicados → aprobar asociando a la carta existente.
export const DuplicatesButton = ({ c, onDone }) => {
  const [rows, setRows] = useState(null);
  const look = async () => {
    try { const { data } = await api.get(`/admin/custom-cards/${c.id}/duplicates`); setRows(data); }
    catch { toast.error("No se pudo buscar duplicados"); }
  };
  const link = async (id) => {
    try {
      const { data } = await api.post(`/admin/custom-cards/${c.id}/approve`, { link_to_card_id: id });
      toast.success(data.publish_pending ? "Asociada · publicación pendiente" : "Asociada y publicada"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo asociar"); }
  };
  if (rows === null) {
    return <Button size="sm" variant="outline" onClick={look} data-testid={`custom-dupes-${c.id}`}>Buscar duplicados</Button>;
  }
  return (
    <div className="w-full p-2 bg-[#0A0A0A] border border-[#262626] rounded text-xs space-y-1" data-testid={`custom-dupes-list-${c.id}`}>
      {rows.length === 0 ? <div className="text-neutral-500">Sin coincidencias en el catálogo.</div> : rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-2">
          <span>{r.name_es || r.name_en} · {r.set_name || "—"} {r.number ? `#${r.number}` : ""} {r.language ? `· ${r.language.toUpperCase()}` : ""}</span>
          <Button size="sm" variant="outline" onClick={() => link(r.id)} data-testid={`custom-link-${c.id}-${r.id}`}>Asociar y aprobar</Button>
        </div>
      ))}
    </div>
  );
};

export const RetryPublishButton = ({ c, onDone }) => {
  if (c.custom_status !== "approved" || !c.pub_draft || c.auto_publication_id) return null;
  const retry = async () => {
    try { await api.post(`/admin/custom-cards/${c.id}/retry-publish`); toast.success("Publicada"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sigue fallando"); }
  };
  return (
    <div className="w-full flex items-center gap-2 text-xs text-amber-300" data-testid={`custom-publish-pending-${c.id}`}>
      Aprobada · publicación pendiente{c.auto_publish_error ? `: ${c.auto_publish_error}` : ""}
      <Button size="sm" onClick={retry} className="bg-amber-600 hover:bg-amber-700" data-testid={`custom-retry-publish-${c.id}`}>Reintentar</Button>
    </div>
  );
};
